/**
 * RecordingListItem - Single recording display with edit mode
 * Extracted from RecordingsTab.jsx
 */
import { Film, Eye, EyeOff, Activity, Trash2, Youtube, Edit, Save, Video } from "lucide-react";
import { Button } from "../../ui/button";
import { ImageUpload } from "../ImageUpload";
import { formatDuration, formatViews } from "../utils";

export const RecordingListItem = ({ 
  recording, 
  isEditing, 
  editForm, 
  setEditForm,
  onEditStart,
  onEditSave,
  onEditCancel,
  onToggleFeatured,
  onPublish,
  onUnpublish,
  onDelete
}) => (
  <div className="flex items-center gap-4 p-4 bg-zinc-900/50 border border-white/5 hover:border-white/20 transition-colors">
    {/* Thumbnail */}
    <div className="w-32 aspect-video bg-zinc-800 flex-shrink-0 relative overflow-hidden">
      {recording.thumbnail_url ? (
        <img src={recording.thumbnail_url} alt={recording.title} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Film className="w-6 h-6 text-zinc-700" />
        </div>
      )}
      {recording.status === "recording" && (
        <div className="absolute top-1 left-1 bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white flex items-center gap-1">
          <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span> REC
        </div>
      )}
    </div>

    {/* Info */}
    <div className="flex-1 min-w-0">
      {isEditing ? (
        // Edit Mode
        <div className="space-y-2">
          <input
            type="text"
            value={editForm.title}
            onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
            className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-1.5 text-sm"
            placeholder="Title"
          />
          <div className="flex items-center gap-2">
            <Video className="w-4 h-4 text-orange-500 flex-shrink-0" />
            <input
              type="text"
              value={editForm.video_url}
              onChange={(e) => setEditForm({ ...editForm, video_url: e.target.value })}
              className="flex-1 bg-zinc-800 border border-white/10 text-white px-3 py-1.5 text-sm"
              placeholder="Video URL (MP4)"
              data-testid="recording-video-url-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Youtube className="w-4 h-4 text-red-500 flex-shrink-0" />
            <input
              type="text"
              value={editForm.youtube_url}
              onChange={(e) => setEditForm({ ...editForm, youtube_url: e.target.value })}
              className="flex-1 bg-zinc-800 border border-white/10 text-white px-3 py-1.5 text-sm"
              placeholder="YouTube URL (optional)"
              data-testid="recording-youtube-url-input"
            />
          </div>
          {/* Thumbnail Upload */}
          <ImageUpload
            value={editForm.thumbnail_url}
            onChange={(url) => setEditForm({ ...editForm, thumbnail_url: url })}
            label="Thumbnail (1920x1080)"
            placeholder="Thumbnail URL"
            previewSize="md"
          />
          <div className="flex items-center gap-2">
            <Button
              onClick={() => onEditSave(recording.id)}
              className="bg-green-600 text-white hover:bg-green-700 text-xs px-3 py-1 h-auto rounded-none flex items-center gap-1"
            >
              <Save className="w-3 h-3" /> Save
            </Button>
            <button onClick={onEditCancel} className="text-zinc-500 text-xs hover:text-white">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        // View Mode
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-secondary text-sm font-bold uppercase tracking-wide text-white truncate">{recording.title}</h4>
            {recording.is_published && (
              <span className="bg-blue-500 text-white px-1.5 py-0.5 text-[10px] font-bold uppercase">Published</span>
            )}
            {recording.is_featured && (
              <span className="bg-yellow-500 text-black px-1.5 py-0.5 text-[10px] font-bold uppercase">Featured</span>
            )}
            <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase ${
              recording.status === 'ready' ? 'bg-green-500/20 text-green-400' :
              recording.status === 'processing' ? 'bg-yellow-500/20 text-yellow-400' :
              recording.status === 'recording' ? 'bg-red-500/20 text-red-400' :
              'bg-zinc-500/20 text-zinc-400'
            }`}>{recording.status}</span>
          </div>
          <div className="flex items-center gap-4 mt-1 text-xs text-zinc-500">
            {recording.sport && <span>{recording.sport}</span>}
            {recording.recorded_at && <span>{recording.recorded_at.split('T')[0]}</span>}
            <span>{formatDuration(recording.duration_seconds)}</span>
            <span>{formatViews(recording.view_count)} views</span>
            {recording.video_url && (
              <span className="flex items-center gap-1 text-orange-500">
                <Video className="w-3 h-3" /> MP4
              </span>
            )}
            {recording.youtube_url && (
              <span className="flex items-center gap-1 text-red-500">
                <Youtube className="w-3 h-3" /> YouTube
              </span>
            )}
          </div>
          {/* Show teams if available */}
          {(recording.home_team || recording.away_team) && (
            <div className="flex items-center gap-2 mt-1 text-xs text-zinc-400">
              {recording.away_team?.name && <span>{recording.away_team.abbreviation || recording.away_team.name}</span>}
              <span className="text-zinc-600">@</span>
              {recording.home_team?.name && <span>{recording.home_team.abbreviation || recording.home_team.name}</span>}
              {recording.final_away_score !== null && recording.final_home_score !== null && (
                <span className="ml-2 font-bold">
                  {recording.final_away_score} - {recording.final_home_score}
                </span>
              )}
            </div>
          )}
        </>
      )}
    </div>

    {/* Actions */}
    {!isEditing && (
      <div className="flex items-center gap-2">
        <button 
          onClick={() => onEditStart(recording)}
          className="p-2 text-zinc-500 hover:text-white transition-colors"
          title="Edit recording"
        >
          <Edit className="w-4 h-4" />
        </button>
        {recording.status === 'ready' && (
          <button 
            onClick={() => recording.is_published ? onUnpublish(recording.id) : onPublish(recording.id)}
            className={`p-2 transition-colors ${recording.is_published ? 'text-blue-500 hover:text-blue-400' : 'text-zinc-500 hover:text-blue-400'}`}
            title={recording.is_published ? "Unpublish from On Demand" : "Publish to On Demand"}
          >
            {recording.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
        <button 
          onClick={() => onToggleFeatured(recording.id)}
          className={`p-2 transition-colors ${recording.is_featured ? 'text-yellow-500 hover:text-yellow-400' : 'text-zinc-500 hover:text-white'}`}
          title={recording.is_featured ? "Remove from featured" : "Add to featured"}
        >
          <Activity className="w-4 h-4" />
        </button>
        <button 
          onClick={() => onDelete(recording.id)}
          className="p-2 text-zinc-500 hover:text-red-400 transition-colors"
          title="Delete recording"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    )}
  </div>
);

export default RecordingListItem;
